<template>
  <div class="sticky-header-container" :class="{ 'is-sticky': isSticky && !forceHide, 'desktop-only': desktopOnly }">
    <slot />
  </div>
</template>

<script setup lang="ts">
import { ref, watch, onMounted, onUnmounted } from 'vue';
import { useIntersectionObserver } from '@vueuse/core';

const props = withDefaults(
  defineProps<{
    anchorRef?: HTMLElement | null;
    desktopOnly?: boolean;
    rootMargin?: string;
    forceHide?: boolean;
  }>(),
  {
    anchorRef: null,
    desktopOnly: true,
    rootMargin: '0px',
    forceHide: false,
  },
);

const isSticky = ref(false);
let stopObserver: (() => void) | null = null;

const emit = defineEmits<(e: 'sticky-change', isSticky: boolean) => void>();

watch(isSticky, (val) => {
  emit('sticky-change', val);
});

const setupObserver = (target: HTMLElement | null) => {
  if (stopObserver) {
    stopObserver();
  }

  if (!target) return;

  const { stop } = useIntersectionObserver(
    target,
    ([{ isIntersecting }]) => {
      isSticky.value = !isIntersecting;
    },
    {
      rootMargin: props.rootMargin,
      threshold: 0,
    },
  );

  stopObserver = stop;
};

onMounted(() => {
  if (props.anchorRef) {
    setupObserver(props.anchorRef);
  }
});

watch(
  () => props.anchorRef,
  (newTarget) => {
    setupObserver(newTarget || null);
  },
);

onUnmounted(() => {
  if (stopObserver) {
    stopObserver();
  }
});
</script>

<style lang="scss" scoped>
@import '~settingSCSS';

.sticky-header-container {
  position: fixed;
  top: 0;
  left: 0;
  width: 100%;
  z-index: 20;
  visibility: hidden;
  transform: translateY(-100%);
  transition:
    transform $sticky-header-transition,
    visibility $sticky-header-transition;

  &.is-sticky {
    visibility: visible;
    transform: translateY(0);
  }

  &.desktop-only {
    display: none;

    @include breakpoint(medium up) {
      display: flex;
    }
  }
}
</style>
